/*
 * TDX vehicle-aware search (tdx-search.js)
 * ========================================
 * Shopify's storefront search ANDs loose words, so "mazda 2 headlight" matches every Mazda
 * with any word starting "2" (2014, 205/60...) and ranks Mazda 3/5 headlights beside the
 * Mazda 2 ones. Quoting the vehicle fixes it, and titles spell some models several ways
 * (CX-5 / CX5, CR-V / CRV, RAV4 / RAV-4), so a detected vehicle is rewritten as an OR group:
 *
 *     mazda 2 headlight   ->  ("Mazda 2") "headlight"
 *     honda crv mirror    ->  ("Honda CR-V" OR "cr-v" OR crv OR "cr v") "mirror"
 *
 * Only the measured ALT_MODELS get the extra spellings; see there for why.
 *
 * The first phrase in the group is always the canonical "Make Model". Liquid reads it back
 * (snippets/tdx-search-terms.liquid, tdx-search-assist.liquid) to show clean terms in the box
 * and to find the vehicle's generation pages — so keep that shape if you change the format.
 *
 * The other words are matched exactly (see strictWords), which makes the results precise
 * enough to list in stock first without burying the real part:
 *
 *     mazda 2 offside headlight -> ("Mazda 2" OR mazda2) ("offside" OR "drivers" ...) ("headlight" OR "headlamp")
 *
 * Sold-out products (options[unavailable_products]):
 *   every search    -> "last"  in stock first, then sold out (owner's call, 2026-09-24)
 *   loose retry     -> "show"  a strict search found nothing, so the words are re-sent as
 *                              typed; loose matches need relevance order, not stock order
 *   "In stock only" -> "hide"  the shopper's own toggle, remembered for the session
 *
 * Vocabulary: assets/vehicle-form-data.json (makes -> models), the same file the vehicle
 * enquiry forms use. If it fails to load or changes shape, search falls back to Shopify's
 * native behaviour — nothing breaks.
 */
(() => {
  if (window.tdxSearch) return;

  const script = document.currentScript;
  const VOCAB_URL = script && script.dataset.vocab;
  const PARAM = 'options[unavailable_products]';
  const PREF_KEY = 'tdx-search-instock';
  const LOOSE_PARAM = 'tdx_loose';
  const YEAR_PARAM = 'tdx_year';
  const MODE = { general: 'last', loose: 'show', instock: 'hide' };

  // Model names that are ordinary words: never detected WITHOUT the make ("leaf spring",
  // "note"), still detected with it ("nissan leaf").
  const MODEL_ONLY_BLOCK = new Set(['note', 'leaf', 'soul', 'splash', 'insight', 'matrix', 'colt', 'alto', 'gen2']);

  // Title spellings the vocabulary doesn't produce (listings say "VELLFIRE"). Keyed by the
  // compact make+model; each alias is also detected when typed.
  const ALIASES = { toyotaalphardvelfire: ['vellfire'] };

  // Models whose OTHER spellings go into the search. Everyone else searches as one exact
  // phrase, ("Mazda 3"), because Shopify loosens every phrase inside an OR group: "Mazda 3"
  // stops needing its words side by side and the "3" gets typo tolerance, so MAZDA CX-3 and
  // MAZDA 5 listings matched a Mazda 3 search - even ("Mazda 3" OR "zzqqxx") leaked.
  // Measured 2026-09-24 on all 147 models: plain phrase 8,176 right / 122 wrong-vehicle
  // results; with spellings 8,007 / 356 (Mazda 5 +95 MX-5 parts, ix35 lost 109 of its own).
  // This list is the 21 models where the spellings found MORE stock and added no wrong ones.
  // Anything not listed, including a model added to the vocabulary later, gets the phrase.
  const ALT_MODELS = new Set([
    'kiacarnival', 'kiaproceed', 'kiaproceedgt', 'kiasorento', 'kiaxceed',
    'hyundaiiload', 'hyundaitucson', 'hyundaii800',
    'mazdacx5', 'mazdamx5',
    'nissanpixo', 'nissanxtrail',
    'suzukivitara',
    'hondacrv', 'hondajazz',
    'toyotaalphardvelfire', 'toyotaprius', 'toyotayaris', 'toyotapriusplus',
    'lexusct200h',
    'mitsubishioutlander',
  ]);

  const compact = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
  // Bare words stay bare; anything with a space or hyphen becomes a phrase ("cx-5", "cr v").
  const quote = (s) => (/[^a-z0-9]/i.test(s) ? `"${s}"` : s);

  let index = null;
  let loading = null;

  function spellings(model) {
    const m = model.toLowerCase().trim();
    const out = new Set();
    m.split('/').forEach((part) => {
      const p = part.trim();
      if (!p) return;
      out.add(p);
      out.add(compact(p));
      if (p.includes('-')) out.add(p.replace(/-/g, ' '));
      // RAV4 -> RAV-4 / RAV 4, IS250 -> IS 250 (2+ letters, then digits, optional letter)
      const c = compact(p);
      const b = c.match(/^([a-z]{2,})(\d+[a-z]?)$/);
      if (b) {
        out.add(`${b[1]}-${b[2]}`);
        out.add(`${b[1]} ${b[2]}`);
      }
    });
    return [...out].filter(Boolean);
  }

  function buildIndex(data) {
    const models = data && data.models;
    if (!models || typeof models !== 'object') return null;
    const byKey = new Map();
    const modelOnlyCount = new Map();
    const entries = [];
    Object.keys(models).forEach((make) => {
      const list = Array.isArray(models[make]) ? models[make] : Object.keys(models[make] || {});
      list.forEach((model) => {
        if (typeof model !== 'string' || !model.trim()) return;
        const c = compact(model);
        // Numeric or 1-2 character models ("2", "IQ") mean nothing without the make.
        const needsMake = /^\d+$/.test(c) || c.length <= 2 || MODEL_ONLY_BLOCK.has(c);
        const canonical = `${make} ${model}`;
        // Other spellings only for the measured ALT_MODELS (see there); aliases likewise.
        const aliases = ALIASES[compact(make + model)] || [];
        const alts = ALT_MODELS.has(compact(make + model)) && !needsMake ? [...spellings(model), ...aliases] : [];
        const entry = { make, model, canonical, alts, needsMake };
        entries.push(entry);
        byKey.set(compact(make + model), entry);
        aliases.forEach((a) => byKey.set(compact(a), entry));
        model.split('/').forEach((part) => byKey.set(compact(make + part), entry));
        if (!needsMake) {
          model.split('/').forEach((part) => {
            const k = compact(part);
            modelOnlyCount.set(k, (modelOnlyCount.get(k) || 0) + 1);
          });
        }
      });
    });
    // Model-only keys, unless two makes share the name.
    entries.forEach((e) => {
      if (e.needsMake) return;
      e.model.split('/').forEach((part) => {
        const k = compact(part);
        if (modelOnlyCount.get(k) === 1 && !byKey.has(k)) byKey.set(k, e);
      });
    });
    return byKey;
  }

  function loadVocab() {
    if (index || loading || !VOCAB_URL) return loading || Promise.resolve(index);
    loading = fetch(VOCAB_URL, { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        index = buildIndex(data);
        return index;
      })
      .catch(() => null);
    return loading;
  }

  // Already rewritten, or the shopper wrote their own search syntax: leave it alone.
  const isRewritten = (q) => /^\s*\(/.test(q);
  const hasSyntax = (q) => /["()]|\bOR\b|\bAND\b|\bNOT\b/.test(q);

  function detect(q) {
    if (!index) return null;
    const tokens = q.trim().split(/\s+/).filter(Boolean);
    for (let i = 0; i < tokens.length; i++) {
      for (let n = Math.min(4, tokens.length - i); n >= 1; n--) {
        const key = compact(tokens.slice(i, i + n).join(''));
        if (!key) continue;
        const entry = index.get(key);
        if (entry) return { entry, start: i, end: i + n, tokens };
      }
    }
    return null;
  }

  // "97701D7600", "BHN3-51030", "33100-T1G-E01": 6+ characters with 4+ digits, not a
  // year range. Left loose: titles write the same number with and without hyphens.
  const isPartNumber = (t) =>
    /^[a-z0-9-]{6,}$/i.test(t) && (t.match(/\d/g) || []).length >= 4 && !/^\d{4}(-\d{2,4})?$/.test(t);

  // ---- part words: exact, with synonyms ----------------------------------------------
  // Unquoted, Shopify's typo tolerance matches "headlight" to brake and roof lights, so with
  // in-stock-first ordering those crowd the real part off page 1 ("i20 alloy": 3 of 24 were
  // alloys). Quoted, each word must appear (plurals still match: "alloys" finds ALLOY) -
  // measured 2026-09-24: i20 alloy 205 -> 30 results, 24/24 alloys on page 1. Quoting also
  // kills typo tolerance, so the words shoppers use but titles don't get synonyms here
  // (no title says "offside" or "nearside"; they say DRIVERS / PASSENGERS), and a search
  // that still finds nothing is retried loosely on the results page.
  // Plural covers singular (Shopify stems quoted words). No "o/s" / "n/s": a slash inside a
  // quoted phrase shrank the whole group (Sportage 119 -> 102) and neither is in any title.
  const SIDE_O = ['drivers', 'driverside', 'offside', 'osf', 'osr', 'right'];
  const SIDE_N = ['passengers', 'passneger', 'nearside', 'nsf', 'nsr', 'left'];
  const HEADLIGHT = ['headlight', 'headlamp'];
  const TAIL = ['tail light', 'rear light', 'rear outer', 'brake light'];
  const RADIO = ['head unit', 'radio', 'stereo'];
  const AIRCON = ['compressor', 'ac pump', 'air con', 'aircon'];
  const SPEEDO = ['instrument cluster', 'speedo', 'clocks', 'cluster'];
  const WORDS = {
    offside: SIDE_O, os: SIDE_O, 'o/s': SIDE_O, driver: SIDE_O, drivers: SIDE_O, driverside: SIDE_O,
    rh: SIDE_O, rhs: SIDE_O, right: SIDE_O,
    nearside: SIDE_N, ns: SIDE_N, 'n/s': SIDE_N, passenger: SIDE_N, passengers: SIDE_N,
    lh: SIDE_N, lhs: SIDE_N, left: SIDE_N,
    headlight: HEADLIGHT, headlights: HEADLIGHT, headlamp: HEADLIGHT, headlamps: HEADLIGHT,
    taillight: TAIL, taillights: TAIL, taillamp: TAIL,
    alloy: ['alloy', 'rim'], alloys: ['alloy', 'rim'], rim: ['alloy', 'rim'], rims: ['alloy', 'rim'],
    radio: RADIO, stereo: RADIO, satnav: ['sat nav', 'head unit'],
    turbo: ['turbo', 'turbocharger'], turbocharger: ['turbo', 'turbocharger'],
    aircon: AIRCON, compressor: AIRCON,
    speedo: SPEEDO, clocks: SPEEDO, cluster: SPEEDO,
    grill: ['grille', 'grill'], grille: ['grille', 'grill'],
  };
  // Two-word forms, checked before single words. "wing mirror" drops "wing": many titles
  // say "POWER FOLD MIRROR" without it.
  const PHRASES = {
    'head light': HEADLIGHT, 'head lights': HEADLIGHT, 'head lamp': HEADLIGHT,
    'tail light': TAIL, 'tail lights': TAIL, 'rear light': TAIL, 'rear lights': TAIL,
    'brake light': TAIL, 'back light': TAIL, 'back lights': TAIL,
    'head unit': RADIO, 'sat nav': ['sat nav', 'head unit'], 'air con': AIRCON, 'ac pump': AIRCON,
    'ac compressor': AIRCON, 'instrument cluster': SPEEDO,
    'wing mirror': ['mirror'], 'door mirror': ['mirror'], 'wing mirrors': ['mirror'],
  };
  // Corner codes ("osf" = offside front) match the SIDE only. Requiring "front" as well
  // zeroed "qashqai osf wing mirror": mirror titles never say front or rear.
  const CORNERS = { osf: SIDE_O, osr: SIDE_O, nsf: SIDE_N, nsr: SIDE_N };

  // A group leads with what the shopper typed, so the first alternative of every group
  // reads back as their words (snippets/tdx-search-terms.liquid, cleanTerms below).
  function orGroup(words) {
    const seen = new Set();
    const list = words.filter((w) => {
      const k = w.toLowerCase();
      if (k.includes('/')) return false; // see SIDE_O: a slash inside a phrase shrinks the group
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    const quoted = list.map((w) => `"${w}"`);
    return quoted.length === 1 ? quoted[0] : `(${quoted.join(' OR ')})`;
  }

  function strictWords(tokens, keepLastLoose) {
    const out = [];
    for (let i = 0; i < tokens.length; i++) {
      const t = tokens[i];
      const low = t.toLowerCase();
      const lastIndex = tokens.length - 1;
      if (!/[a-z0-9]/i.test(t)) continue;
      if (keepLastLoose && i === lastIndex) {
        out.push(t); // still being typed: Shopify prefix-matches the last word
        continue;
      }
      const pair = i < lastIndex ? `${low} ${tokens[i + 1].toLowerCase()}` : '';
      if (pair && PHRASES[pair] && !(keepLastLoose && i + 1 === lastIndex)) {
        out.push(orGroup([pair, ...PHRASES[pair]]));
        i++;
        continue;
      }
      if (CORNERS[low]) {
        out.push(orGroup([low, ...CORNERS[low]]));
        continue;
      }
      if (isPartNumber(t)) {
        out.push(t);
        continue;
      }
      out.push(WORDS[low] ? orGroup([low, ...WORDS[low]]) : `"${t}"`);
    }
    return out;
  }

  /** The words behind a rewritten query: the first alternative of every group, unquoted. */
  function cleanTerms(q) {
    return String(q || '')
      .replace(/\(([^()]*)\)/g, (m, inner) => inner.split(' OR ')[0])
      .replace(/"/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Rewrite a raw query. Returns { q, vehicle, loose }. vehicle is the canonical
   * "Make Model" or null. opts.loose keeps the other words as typed (the zero-result retry);
   * opts.typing leaves the last word loose for the as-you-type dropdown.
   */
  // A typed year leaves the query and travels as tdx_year. Shopify's own year matching is
  // fine alone but collapses next to an OR group (measured 2026-09-24: civic 2018
  // ("headlight" OR "headlamp") -> 4 results; without the year -> 13, all 7 that fit 2018
  // among them). So search for the part, then use the year on the page: the fitting
  // generation chip first, fitting listings first (applyYear).
  const isYear = (t) => /^(19[89]\d|20[0-3]\d)$/.test(t);

  function rewrite(raw, opts = {}) {
    const q = String(raw || '').trim();
    const loose = !!opts.loose;
    const none = { q, vehicle: null, loose, year: null };
    if (!q) return none;
    if (isRewritten(q)) {
      const m = q.match(/^\s*\(\s*"([^"]+)"/);
      return { ...none, vehicle: m ? m[1] : null };
    }
    if (hasSyntax(q)) return none;
    const hit = detect(q);
    if (!hit) return none;
    const { entry, start, end, tokens } = hit;
    const group = [`"${entry.canonical}"`, ...entry.alts.map(quote)];
    const all = tokens.slice(0, start).concat(tokens.slice(end));
    const year = all.find(isYear) || null;
    const rest = all.filter((t) => !isYear(t));
    const words = loose ? rest : strictWords(rest, !!opts.typing);
    return {
      q: `(${group.join(' OR ')})${words.length ? ' ' + words.join(' ') : ''}`,
      vehicle: entry.canonical,
      loose,
      year,
    };
  }

  function readPref() {
    try {
      return window.sessionStorage.getItem(PREF_KEY) === '1';
    } catch (e) {
      return false;
    }
  }
  function writePref(on) {
    try {
      if (on) window.sessionStorage.setItem(PREF_KEY, '1');
      else window.sessionStorage.removeItem(PREF_KEY);
    } catch (e) {
      /* storage blocked: the toggle still works for this page */
    }
  }

  function modeFor(r) {
    if (readPref()) return MODE.instock;
    return r && r.loose ? MODE.loose : MODE.general;
  }

  /** For predictive-search.js: the query + extra params for /search/suggest. */
  function predictive(term) {
    loadVocab();
    const r = rewrite(term, { typing: true });
    return {
      q: r.q,
      extra: `&${encodeURIComponent('resources[options][unavailable_products]')}=${modeFor(r)}`,
    };
  }

  // ---- form submits (header, drawer, search page, predictive "Search for ...") ----------

  function isSearchForm(form) {
    if (!form || form.method.toLowerCase() !== 'get') return false;
    const searchPath = (window.routes && window.routes.search_url) || '/search';
    try {
      return new URL(form.action, location.href).pathname.replace(/\/$/, '') === searchPath.replace(/\/$/, '');
    } catch (e) {
      return false;
    }
  }

  function setHidden(form, name, value) {
    let el = [...form.querySelectorAll('input[type="hidden"]')].find((i) => i.name === name);
    if (!el) {
      el = document.createElement('input');
      el.type = 'hidden';
      el.name = name;
      el.dataset.tdxAdded = '1';
      form.appendChild(el);
    }
    el.value = value;
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!isSearchForm(form)) return;
      const input = form.querySelector('input[name="q"]');
      if (!input) return;
      const r = rewrite(input.value);
      setHidden(form, PARAM, modeFor(r));
      if (r.year) setHidden(form, YEAR_PARAM, r.year);
      else form.querySelectorAll(`input[name="${YEAR_PARAM}"]`).forEach((el) => el.remove());
      if (r.q !== input.value.trim()) {
        // Submit the rewrite through a hidden field so the visible box keeps what the
        // shopper typed (and still does if they come Back to this page).
        input.dataset.tdxName = 'q';
        input.removeAttribute('name');
        setHidden(form, 'q', r.q);
      }
    },
    true
  );

  window.addEventListener('pageshow', () => {
    document.querySelectorAll('input[data-tdx-name]').forEach((input) => {
      input.name = input.dataset.tdxName;
      delete input.dataset.tdxName;
    });
    document.querySelectorAll('input[data-tdx-added="1"][name="q"]').forEach((el) => el.remove());
  });

  document.addEventListener(
    'focusin',
    (event) => {
      const t = event.target;
      if (t && t.matches && t.matches('input[type="search"], input[name="q"]')) loadVocab();
    },
    { passive: true }
  );

  // ---- search results page -----------------------------------------------------------

  function onSearchPage() {
    const searchPath = ((window.routes && window.routes.search_url) || '/search').replace(/\/$/, '');
    return location.pathname.replace(/\/$/, '') === searchPath;
  }

  // ---- the typed year, applied on the page --------------------------------------------

  const yearsFrom = (text) => {
    const m = String(text || '').match(/((?:19|20)\d\d)\s*[-\u2013\/]\s*((?:19|20)?\d\d)/);
    if (!m) return null;
    const a = Number(m[1]);
    const b = m[2].length === 4 ? Number(m[2]) : Number(String(a).slice(0, 2) + m[2]);
    return [a, b];
  };
  const within = (range, y) => !!range && range[0] <= y && y <= range[1];

  // "Honda Civic headlamp" -> "Honda Civic 2018 headlamp": the year goes back after the
  // vehicle, where it was typed (the query itself no longer carries it).
  function withYear(text, vehicle, yearText) {
    const v = String(text || '').trim();
    if (!v || v.includes(yearText)) return v;
    return vehicle && v.startsWith(vehicle) ? `${vehicle} ${yearText}${v.slice(vehicle.length)}` : `${v} ${yearText}`;
  }

  function applyYear(yearText) {
    const y = Number(yearText);
    const assist = document.querySelector('.tdx-sa');
    const vehicle = (assist && assist.dataset.vehicle) || '';

    document.querySelectorAll('input[name="q"], input[data-tdx-name="q"]').forEach((input) => {
      if (input.type !== 'hidden') input.value = withYear(input.value, vehicle, yearText);
    });
    const t = document.title.match(/^(Search: )(.*?)( \u2013 .*)?$/);
    if (t) document.title = t[1] + withYear(t[2], vehicle, yearText) + (t[3] || '');

    // Generation chips: mark the fitting one(s), move them first, and swap in the facelift
    // page when the year sits on one side only (a boundary year keeps the generation page).
    const box = document.querySelector('.tdx-sa__gens');
    if (box) {
      const fit = [...box.children].filter((c) => within(yearsFrom(c.dataset.years), y));
      fit.forEach((c) => {
        c.classList.add('is-fit');
        const pre = within(yearsFrom(c.dataset.pre), y);
        const fl = within(yearsFrom(c.dataset.fl), y);
        const url = pre && !fl ? c.dataset.preUrl : fl && !pre ? c.dataset.flUrl : '';
        if (url && c.tagName === 'A') {
          c.href = url;
          c.insertAdjacentHTML('beforeend', ` <em>${pre ? 'Pre-facelift' : 'Facelift'}</em>`);
        }
      });
      fit.reverse().forEach((c) => box.prepend(c));
      const title = document.querySelector('.tdx-sa__title');
      if (fit.length && title) {
        const what = title.dataset.what ? ` ${title.dataset.what}` : '';
        title.textContent = '';
        title.append('Best match for your ');
        const strong = document.createElement('strong');
        strong.textContent = `${yearText} ${vehicle}`;
        title.append(strong, `${what}, first`);
      }
    }

    // Listings on this page: in stock first stays the rule; within each half, parts whose
    // title range covers the year come first, then titles with no range, then the rest.
    // Pages and guides go after the parts. (Tell them apart by the link: some sold-out
    // product cards render no price element.)
    const grid = document.querySelector('#product-grid ul.product-grid');
    if (grid) {
      const rank = (li) => {
        if (!li.querySelector('a[href*="/products/"]')) return 9;
        const sold = /\bSold out\b/i.test(li.textContent) ? 3 : 0;
        const r = yearsFrom((li.querySelector('.card__heading') || li).textContent);
        return sold + (r ? (within(r, y) ? 0 : 2) : 1);
      };
      [...grid.children]
        .map((li, i) => ({ li, i, k: rank(li) }))
        .sort((a, b) => a.k - b.k || a.i - b.i)
        .forEach(({ li }) => grid.appendChild(li));
    }
  }

  function initResultsPage() {
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    if (!q) return;

    // Arrivals that skipped our forms (links from Google, ChatGPT, suggested queries, old
    // bookmarks): apply the same rewrite + sold-out rule, once. After the replace the URL
    // carries both, so this cannot loop.
    if (!params.has(PARAM)) {
      loadVocab().then(() => {
        const r = rewrite(q);
        params.set('q', r.q);
        params.set(PARAM, modeFor(r));
        if (r.year) params.set(YEAR_PARAM, r.year);
        location.replace(`${location.pathname}?${params.toString()}`);
      });
      return;
    }

    const assist = document.querySelector('.tdx-sa');
    const count = assist ? Number(assist.dataset.results) : NaN;
    const retried = params.get(LOOSE_PARAM) === '1';
    const year = isYear(params.get(YEAR_PARAM) || '') ? params.get(YEAR_PARAM) : null;

    // Exact words found nothing (a typo, or a word no title uses): retry once with the
    // words as typed. The vehicle stays quoted, so it is still only that car's parts.
    if (count === 0 && !retried && isRewritten(q) && /\)\s*\S/.test(q)) {
      loadVocab().then(() => {
        const r = rewrite(cleanTerms(q), { loose: true });
        if (!r.vehicle || r.q === q) return;
        params.set('q', r.q);
        params.set(PARAM, modeFor(r));
        params.set(LOOSE_PARAM, '1');
        params.delete('page');
        location.replace(`${location.pathname}?${params.toString()}`);
      });
      return;
    }

    if (year) applyYear(year);

    // No retry left to make: show the "search all words" way out, if the page has one.
    const broader = document.querySelector('.tdx-sa__broader');
    if (broader) broader.hidden = false;

    const mode = params.get(PARAM);
    // Shopify's pager keeps only its own parameters: put ours back on every page link.
    document.querySelectorAll('.pagination a[href], a.pagination__item[href]').forEach((a) => {
      const u = new URL(a.getAttribute('href'), location.href);
      if (year) u.searchParams.set(YEAR_PARAM, year);
      if (retried) u.searchParams.set(LOOSE_PARAM, '1');
      a.setAttribute('href', u.pathname + u.search);
    });
    // Carry the mode (and the retry marker) through sort / filter changes, which rebuild the
    // URL from these forms.
    document.querySelectorAll('#FacetFiltersForm, #FacetFiltersFormMobile, #FacetSortForm, .facets-vertical-form').forEach(
      (form) => {
        setHidden(form, PARAM, mode);
        if (retried) setHidden(form, LOOSE_PARAM, '1');
        if (year) setHidden(form, YEAR_PARAM, year);
      }
    );

    const slot = document.querySelector('[data-tdx-stock-toggle]');
    if (!slot) return;
    const id = 'tdx-instock-only';
    const note =
      mode === MODE.instock
        ? 'Sold-out parts are hidden.'
        : retried
        ? 'No exact match for every word, so these are the closest matches.'
        : 'In-stock parts first, then sold out. Open a sold-out part to request it or find an in-stock match.';
    slot.innerHTML =
      `<label class="tdx-sa__stock" for="${id}">` +
      `<input type="checkbox" id="${id}"${mode === MODE.instock ? ' checked' : ''}> In stock only</label>` +
      `<span class="tdx-sa__stock-note">${note}</span>`;
    slot.hidden = false;
    slot.querySelector('input').addEventListener('change', (e) => {
      writePref(e.target.checked);
      params.set(PARAM, modeFor({ loose: retried }));
      params.delete('page');
      location.assign(`${location.pathname}?${params.toString()}`);
    });
  }

  window.tdxSearch = { rewrite, predictive, loadVocab, modeFor, cleanTerms };

  if (onSearchPage()) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initResultsPage);
    else initResultsPage();
  }
})();
